import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '.';
import type { AiChatMetadata } from '@server/ai';
import type { SfAppElement } from '../components/pages/ai-assistant/apps/AppsConnectionDialog';

export interface SupportAgentStateByOrg {
  apps?: SfAppElement[];
  isLoadingApps?: boolean;
  chats?: AiChatMetadata[];
  isLoadingChats?: boolean;
}

export interface SupportAgentState {
  [crmOrgId: string]: SupportAgentStateByOrg;
}

const initialState: SupportAgentState = {};

export const supportAgentSlice = createSlice({
  name: 'supportAgent',
  initialState,
  reducers: {
    setAppsLoading: (state, action: PayloadAction<{ crmOrgId: string; isLoading: boolean }>) => {
      state[action.payload.crmOrgId] = {
        ...(state[action.payload.crmOrgId] ?? {}),
        isLoadingApps: action.payload.isLoading,
      };
    },
    setApps: (state, action: PayloadAction<{ crmOrgId: string; apps: SfAppElement[] }>) => {
      state[action.payload.crmOrgId] = {
        ...(state[action.payload.crmOrgId] ?? {}),
        apps: action.payload.apps,
        isLoadingApps: false,
      };
    },
    setChatsLoading: (state, action: PayloadAction<{ crmOrgId: string; isLoading: boolean }>) => {
      state[action.payload.crmOrgId] = {
        ...(state[action.payload.crmOrgId] ?? {}),
        isLoadingChats: action.payload.isLoading,
      };
    },
    setChats: (
      state,
      action: PayloadAction<{ crmOrgId: string; chats: AiChatMetadata[]; agentId: string }>,
    ) => {
      state[action.payload.crmOrgId] = {
        ...(state[action.payload.crmOrgId] ?? {}),
        chats: action.payload.chats,
        isLoadingChats: false,
      };
    },
    clearChatsForOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      state[action.payload.crmOrgId] = {
        ...(state[action.payload.crmOrgId] ?? {}),
        chats: [],
        isLoadingChats: false,
      };
    },
  },
});

export const { setAppsLoading, setApps, setChatsLoading, setChats, clearChatsForOrg } =
  supportAgentSlice.actions;

export const selectSupportAgentApps = (crmOrgId: string) => (state: RootState) =>
  state.supportAgent?.[crmOrgId]?.apps;

export const selectSupportAgentIsLoadingApps = (crmOrgId: string) => (state: RootState) =>
  !!state.supportAgent?.[crmOrgId]?.isLoadingApps;

export const selectSupportAgentChats = (crmOrgId: string) => (state: RootState) =>
  state.supportAgent?.[crmOrgId]?.chats ?? [];

export const selectSupportAgentIsLoadingChats = (crmOrgId: string) => (state: RootState) =>
  !!state.supportAgent?.[crmOrgId]?.isLoadingChats;

export default supportAgentSlice.reducer;
